import { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop";
import HashScrollManager from "./components/HashScrollManager";
import BackToTop from "./components/BackToTop";
import ScrollProgress from "./components/ScrollProgress";
import PageTracker from "./components/PageTracker";
import CookieConsentBanner from "./components/CookieConsentBanner";
import PageLoader from "./components/PageLoader";
import { LeadGateProvider } from "./contexts/LeadGateContext";
import UtmRedirectHandler from "./components/UtmRedirectHandler";
import MetaPixelInitializer from "./components/MetaPixelInitializer";



// Critical: Keep Index eager for fast home page load
import Index from "./pages/Index";

// Lazy-load heavy pages for reduced initial bundle
const PricePage = lazy(() => import("./pages/PricePage"));
const FloorPlansPage = lazy(() => import("./pages/FloorPlansPage"));
const AmenitiesPage = lazy(() => import("./pages/AmenitiesPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const VirtualTourPage = lazy(() => import("./pages/VirtualTourPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const RERAPage = lazy(() => import("./pages/RERAPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const DisclaimerPage = lazy(() => import("./pages/DisclaimerPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const ComparisonPage = lazy(() => import("./pages/ComparisonPage"));
const NRIBuyersPage = lazy(() => import("./pages/NRIBuyersPage"));
const InvestorPage = lazy(() => import("./pages/InvestorPage"));
const WhoItsForPage = lazy(() => import("./pages/WhoItsForPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const NeighbourhoodPage = lazy(() => import("./pages/NeighbourhoodPage"));
const ThreeBHKChandivaliPage = lazy(() => import("./pages/ThreeBHKChandivaliPage"));
const FlatsNearAmritShaktiPage = lazy(() => import("./pages/FlatsNearAmritShaktiPage"));
const UnderConstructionChandivaliPage = lazy(() => import("./pages/UnderConstructionChandivaliPage"));
const NaharComparisonPage = lazy(() => import("./pages/NaharComparisonPage"));
const UnderConstructionNearAmritShaktiPage = lazy(() => import("./pages/UnderConstructionNearAmritShaktiPage"));
const NewProjectsNearPowaiPage = lazy(() => import("./pages/NewProjectsNearPowaiPage"));
const FlatsNearJVLRPage = lazy(() => import("./pages/FlatsNearJVLRPage"));
const InvestmentGuidePage = lazy(() => import("./pages/InvestmentGuidePage"));
const AdminLeadsPage = lazy(() => import("./pages/AdminLeadsPage"));
const AdminEcosystemPage = lazy(() => import("./pages/AdminEcosystemPage"));
const AdminUsersPage = lazy(() => import("./pages/AdminUsersPage"));
const AdminOpsPage = lazy(() => import("./pages/AdminOpsPage"));
const GoldAssurancePage = lazy(() => import("./pages/GoldAssurancePage"));
const SustainabilityPage = lazy(() => import("./pages/SustainabilityPage"));
const FreeholdLandPage = lazy(() => import("./pages/FreeholdLandPage"));
const LowDensityDesignPage = lazy(() => import("./pages/LowDensityDesignPage"));
const ClearHeightPage = lazy(() => import("./pages/ClearHeightPage"));
const CoveredParkingPage = lazy(() => import("./pages/CoveredParkingPage"));
const ThreeAndFourBHKPage = lazy(() => import("./pages/ThreeAndFourBHKPage"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const PricingGuidePage = lazy(() => import("./pages/PricingGuidePage"));
const CompetitorComparisonPage = lazy(() => import("./pages/CompetitorComparisonPage"));
const FBFamilyUpgradePage = lazy(() => import("./pages/FBFamilyUpgradePage"));
const QABreakpointsPage = lazy(() => import("./pages/QABreakpointsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <LeadGateProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollProgress />
            <ScrollToTop />
            <HashScrollManager />
            <BackToTop />
            <PageTracker />
            <UtmRedirectHandler />
            <MetaPixelInitializer />
            
            <CookieConsentBanner />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/price" element={<PricePage />} />
                <Route path="/floor-plans" element={<FloorPlansPage />} />
                <Route path="/amenities" element={<AmenitiesPage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/location" element={<LocationPage />} />
                <Route path="/directions" element={<LocationPage />} />
                <Route path="/virtual-tour" element={<VirtualTourPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/rera" element={<RERAPage />} />
                <Route path="/rera-information" element={<RERAPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/terms-and-conditions" element={<TermsPage />} />
                <Route path="/disclaimer" element={<DisclaimerPage />} />
                <Route path="/cookie-policy" element={<CookiePolicyPage />} />
                {/* SEO Landing Pages */}
                <Route path="/compare" element={<ComparisonPage />} />
                <Route path="/nahar-chandi-valley-vs-other-projects-chandivali" element={<ComparisonPage />} />
                <Route path="/nri-buyers" element={<NRIBuyersPage />} />
                <Route path="/nri-buyers-guide-nahar-chandi-valley" element={<NRIBuyersPage />} />
                <Route path="/investment" element={<InvestorPage />} />
                <Route path="/investment-opportunity-nahar-chandi-valley" element={<InvestorPage />} />
                <Route path="/who-its-for" element={<WhoItsForPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/neighbourhood" element={<NeighbourhoodPage />} />
                <Route path="/nahar-amrit-shakti" element={<NeighbourhoodPage />} />
                {/* SEO Money Pages */}
                <Route path="/3-bhk-in-chandivali" element={<ThreeBHKChandivaliPage />} />
                <Route path="/flats-near-nahar-amrit-shakti" element={<FlatsNearAmritShaktiPage />} />
                <Route path="/under-construction-flats-chandivali" element={<UnderConstructionChandivaliPage />} />
                <Route path="/nahar-amrit-shakti-vs-nahar-chandivali" element={<NaharComparisonPage />} />
                {/* Micro-Location SEO Pages */}
                <Route path="/under-construction-flats-near-nahar-amrit-shakti" element={<UnderConstructionNearAmritShaktiPage />} />
                <Route path="/new-projects-near-powai" element={<NewProjectsNearPowaiPage />} />
                <Route path="/flats-near-jvlr-chandivali" element={<FlatsNearJVLRPage />} />
                <Route path="/chandivali-property-investment-guide" element={<InvestmentGuidePage />} />
                <Route path="/admin/leads/*" element={<AdminLeadsPage />} />
                <Route path="/admin/ecosystem/*" element={<AdminEcosystemPage />} />
                <Route path="/admin/users/*" element={<AdminUsersPage />} />
                <Route path="/admin/ops/*" element={<AdminOpsPage />} />
                <Route path="/gold-assurance" element={<GoldAssurancePage />} />
                <Route path="/sustainability" element={<SustainabilityPage />} />
                <Route path="/miyawaki-forest" element={<SustainabilityPage />} />
                <Route path="/freehold-land" element={<FreeholdLandPage />} />
                <Route path="/low-density-design" element={<LowDensityDesignPage />} />
                <Route path="/clear-height" element={<ClearHeightPage />} />
                <Route path="/covered-parking" element={<CoveredParkingPage />} />
                <Route path="/3-4-bhk-residences" element={<ThreeAndFourBHKPage />} />
                <Route path="/sitemap" element={<SitemapPage />} />
                {/* SEO Authority Hub */}
                <Route path="/nahar-chandi-valley-pricing-guide" element={<PricingGuidePage />} />
                {/* Competitor Comparison Page */}
                <Route path="/compare/nahar-chandi-valley-vs-competitors" element={<CompetitorComparisonPage />} />
                {/* Campaign Landing Pages */}
                <Route path="/fb-family-upgrade" element={<FBFamilyUpgradePage />} />
                {/* Internal QA Tools */}
                <Route path="/qa-breakpoints" element={<QABreakpointsPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </LeadGateProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
